import React, { forwardRef, useState } from 'react';

import { Platform, Switch as RNSwitch, StyleSheet } from 'react-native';
import type { ViewStyle } from 'react-native';

import { getDesignToken, primitive } from 'styles/constants';

import { addWithConfig } from '../../utils';

import type { SwitchProps } from './types';

const scaleStyle: ViewStyle | undefined = Platform.select({
  ios: { transform: [{ scale: 0.8 }] },
  android: { transform: [{ scale: 1.3 }] },
  web: { transform: [{ scale: 1.16 }] },
  default: undefined,
});

const Switch = forwardRef<React.ComponentRef<typeof RNSwitch>, SwitchProps>(function Switch(
  { style, ...props },
  ref
) {
  const {
    disabled,
    isDisabled,
    isChecked,
    defaultIsChecked,
    onToggle,
    value: valueFromProps,
    onValueChange,
    onTrackColor,
    offTrackColor,
    onThumbColor,
    offThumbColor,
    accessibilityLabel,
    accessibilityHint,
    testID,
    'aria-label': ariaLabel,
    ...rest
  } = props;

  const d = Boolean(disabled || isDisabled);
  const [uncontrolled, setUncontrolled] = useState(Boolean(defaultIsChecked));

  const checked =
    isChecked != null ? isChecked : valueFromProps !== undefined ? valueFromProps : uncontrolled;

  const off = offTrackColor as string | undefined;
  const onT = onTrackColor as string | undefined;
  const thumbOn = (onThumbColor as string | undefined) ?? primitive.$white;
  const thumbOff = (offThumbColor as string | undefined) ?? primitive.$white;

  const trackOff = d ? getDesignToken('background-switch-track-disabled') : off;
  const trackOn = d ? getDesignToken('background-switch-track-disabled') : onT;
  const thumbOnResolved = d ? getDesignToken('background-switch-thumb') : thumbOn;
  const thumbOffResolved = d ? getDesignToken('background-switch-thumb') : thumbOff;

  return (
    <RNSwitch
      {...(rest as React.ComponentProps<typeof RNSwitch>)}
      ref={ref}
      testID={testID}
      accessibilityLabel={accessibilityLabel ?? ariaLabel}
      accessibilityHint={accessibilityHint}
      disabled={d}
      value={checked}
      onValueChange={v => {
        onValueChange?.(v);
        onToggle?.();
        if (isChecked == null && valueFromProps === undefined) {
          setUncontrolled(v);
        }
      }}
      trackColor={
        trackOff != null && trackOn != null ? { false: trackOff, true: trackOn } : undefined
      }
      thumbColor={checked ? thumbOnResolved : thumbOffResolved}
      // react-native-web reads this for the "on" thumb; @types/react-native's Switch often omit it
      {...(Platform.OS === 'web' ? { activeThumbColor: thumbOnResolved } : {})}
      ios_backgroundColor={Platform.OS === 'ios' ? undefined : trackOff}
      style={style == null ? scaleStyle : StyleSheet.compose(scaleStyle, style)}
    />
  );
});

export default addWithConfig(Switch);
